import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { subDays, format, parseISO } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useMediaQuery } from 'react-responsive';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Box, Container, Typography, Paper, Grid, Tabs, Tab, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Button, Chip, Switch,
  FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Alert, TextField, Drawer, IconButton, Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import GroupsIcon from '@mui/icons-material/Groups';
import CloseIcon from '@mui/icons-material/Close';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';

// ─── helpers ────────────────────────────────────────────────────────────────

function ratingColor(val) {
  if (val === null || val === undefined) return '#9e9e9e';
  if (val >= 4.5) return '#2e7d32';
  if (val >= 4.0) return '#558b2f';
  if (val >= 3.5) return '#f57c00';
  return '#c62828';
}

function fmtDate(val) {
  if (!val) return '—';
  try { return format(typeof val === 'string' ? parseISO(val) : val, 'MMM d, yyyy'); }
  catch { return val; }
}

function fmtDateTime(val) {
  if (!val) return '—';
  try { return format(typeof val === 'string' ? parseISO(val) : val, 'MMM d, yyyy h:mm a'); }
  catch { return val; }
}

function fmtWeek(val) {
  if (!val) return '';
  try { return format(typeof val === 'string' ? parseISO(val) : val, 'MMM d'); }
  catch { return val; }
}

function buildParams(filters) {
  const p = new URLSearchParams();
  if (filters.startDate) p.set('startDate', format(filters.startDate, 'yyyy-MM-dd'));
  if (filters.endDate) p.set('endDate', format(filters.endDate, 'yyyy-MM-dd'));
  if (filters.store) p.set('store', filters.store);
  if (filters.region) p.set('region', filters.region);
  if (filters.storeType) p.set('storeType', filters.storeType);
  if (filters.districtOperator) p.set('districtOperator', filters.districtOperator);
  return p;
}

// ─── sub-components ──────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color, small }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography variant={small ? 'h5' : 'h4'} fontWeight={700} sx={{ color: color || 'text.primary', lineHeight: 1.2, mt: 0.5 }}>
        {value ?? '—'}
      </Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

function RatingBar({ label, value }) {
  const pct = value ? (value / 5) * 100 : 0;
  const color = ratingColor(value);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={600} sx={{ color }}>
          {value ? Number(value).toFixed(2) : '—'} / 5
        </Typography>
      </Box>
      <Box sx={{ height: 8, borderRadius: 4, bgcolor: '#e0e0e0', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 4, transition: 'width .4s' }} />
      </Box>
    </Box>
  );
}

// ─── filter bar ──────────────────────────────────────────────────────────────

function FilterBar({ filters, setFilters, dimensions, onApply, loading }) {
  const selects = [
    { key: 'region', label: 'Region', options: dimensions.regions || [] },
    { key: 'store', label: 'Store', options: dimensions.stores || [] },
    { key: 'storeType', label: 'Store Type', options: dimensions.storeTypes || [] },
    { key: 'districtOperator', label: 'District Operator', options: dimensions.districts || [] },
  ];

  return (
    <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2, p: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <DatePicker
          selected={filters.startDate}
          onChange={d => setFilters(f => ({ ...f, startDate: d }))}
          selectsStart
          startDate={filters.startDate}
          endDate={filters.endDate}
          dateFormat="MMM d, yyyy"
          customInput={<TextField size="small" label="Start Date" sx={{ width: 150 }} autoComplete="off" />}
        />
        <DatePicker
          selected={filters.endDate}
          onChange={d => setFilters(f => ({ ...f, endDate: d }))}
          selectsEnd
          startDate={filters.startDate}
          endDate={filters.endDate}
          minDate={filters.startDate}
          dateFormat="MMM d, yyyy"
          customInput={<TextField size="small" label="End Date" sx={{ width: 150 }} autoComplete="off" />}
        />
        {selects.map(({ key, label, options }) => (
          <FormControl key={key} size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{label}</InputLabel>
            <Select
              value={filters[key]}
              label={label}
              onChange={e => setFilters(f => ({ ...f, [key]: e.target.value }))}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
            </Select>
          </FormControl>
        ))}
        <Button variant="contained" size="small" onClick={onApply} disabled={loading} startIcon={<RefreshIcon />}>
          Apply
        </Button>
      </Box>
    </Paper>
  );
}

// ─── Overview tab ────────────────────────────────────────────────────────────

function OverviewTab({ data, loading }) {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data) return null;

  const { summary, trend, ratingDistribution } = data;
  const totalInvited = summary.TOTAL_INVITED || 0;
  const totalCompleted = summary.TOTAL_COMPLETED || 0;
  const responseRate = summary.RESPONSE_RATE_PCT;
  const avgHospitality = summary.AVG_HOSPITALITY;
  const avgMeal = summary.AVG_MEAL_QUALITY;
  const avgOverall = summary.AVG_OVERALL;
  const lowRatings = summary.LOW_RATING_COUNT || 0;
  const withFeedback = summary.WITH_FEEDBACK_COUNT || 0;

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Invitations Sent" value={totalInvited.toLocaleString()} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard label="Responses" value={totalCompleted.toLocaleString()} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="Response Rate"
            value={responseRate !== null && responseRate !== undefined ? `${responseRate}%` : '—'}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="Avg Hospitality"
            value={avgHospitality ? Number(avgHospitality).toFixed(2) : '—'}
            sub="of 5"
            color={ratingColor(avgHospitality)}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="Avg Meal Quality"
            value={avgMeal ? Number(avgMeal).toFixed(2) : '—'}
            sub="of 5"
            color={ratingColor(avgMeal)}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <KpiCard
            label="Low Ratings (≤2)"
            value={lowRatings.toLocaleString()}
            sub={`${withFeedback.toLocaleString()} with feedback`}
            color={lowRatings > 0 ? '#c62828' : undefined}
            small
          />
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" mb={1}>Overall Average</Typography>
        <RatingBar label="Overall (avg of both metrics)" value={avgOverall} />
        <RatingBar label="Hospitality" value={avgHospitality} />
        <RatingBar label="Meal Quality" value={avgMeal} />
      </Paper>

      {trend && trend.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" mb={2}>Weekly Rating Trend</Typography>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={trend} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="WEEK" tickFormatter={fmtWeek} tick={{ fontSize: 11 }} />
              <YAxis domain={[1, 5]} tick={{ fontSize: 11 }} />
              <Tooltip
                labelFormatter={(l) => fmtWeek(l)}
                formatter={(v, name) => [Number(v).toFixed(2), name]}
              />
              <Legend />
              <Line type="monotone" dataKey="AVG_HOSPITALITY" name="Hospitality" stroke="#163350" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="AVG_MEAL_QUALITY" name="Meal Quality" stroke="#f57c00" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {ratingDistribution && ratingDistribution.length > 0 && (() => {
        // Force all 5 rating buckets (1-5), filling zeros for any missing so
        // the scale always visibly extends to 5.
        const byRating = new Map(ratingDistribution.map(r => [Number(r.RATING), r]));
        const fullData = [1, 2, 3, 4, 5].map(rating => ({
          RATING: rating,
          HOSPITALITY_COUNT: Number(byRating.get(rating)?.HOSPITALITY_COUNT || 0),
          MEAL_QUALITY_COUNT: Number(byRating.get(rating)?.MEAL_QUALITY_COUNT || 0),
        }));
        return (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" mb={2}>Rating Distribution (out of 5)</Typography>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={fullData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis
                  dataKey="RATING"
                  tick={{ fontSize: 12 }}
                  ticks={[1, 2, 3, 4, 5]}
                  tickFormatter={(v) => `${v} / 5`}
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip labelFormatter={(l) => `Rating ${l} / 5`} />
                <Legend />
                <Bar dataKey="HOSPITALITY_COUNT" name="Hospitality" fill="#163350" />
                <Bar dataKey="MEAL_QUALITY_COUNT" name="Meal Quality" fill="#f57c00" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        );
      })()}
    </Box>
  );
}

// ─── By Store tab ────────────────────────────────────────────────────────────

function StoresTab({ data, loading }) {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data || !data.rows) return null;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Store</TableCell>
            <TableCell>Region</TableCell>
            <TableCell>District Operator</TableCell>
            <TableCell align="right">Responses</TableCell>
            <TableCell align="right">Avg Hospitality</TableCell>
            <TableCell align="right">Avg Meal Quality</TableCell>
            <TableCell align="right">Avg Overall</TableCell>
            <TableCell align="right">Low Ratings</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.length === 0 ? (
            <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary" py={2}>No responses match the current filters.</Typography></TableCell></TableRow>
          ) : data.rows.map(row => (
            <TableRow key={row.STORE_ID} hover>
              <TableCell>{row.STORE_NAME || '—'}</TableCell>
              <TableCell>{row.STORE_REGION || '—'}</TableCell>
              <TableCell>{row.DISTRICT_OPERATOR || '—'}</TableCell>
              <TableCell align="right">{row.RESPONSE_COUNT}</TableCell>
              <TableCell align="right" sx={{ color: ratingColor(row.AVG_HOSPITALITY), fontWeight: 600 }}>
                {row.AVG_HOSPITALITY ? Number(row.AVG_HOSPITALITY).toFixed(2) : '—'}
              </TableCell>
              <TableCell align="right" sx={{ color: ratingColor(row.AVG_MEAL_QUALITY), fontWeight: 600 }}>
                {row.AVG_MEAL_QUALITY ? Number(row.AVG_MEAL_QUALITY).toFixed(2) : '—'}
              </TableCell>
              <TableCell align="right" sx={{ color: ratingColor(row.AVG_OVERALL), fontWeight: 600 }}>
                {row.AVG_OVERALL ? Number(row.AVG_OVERALL).toFixed(2) : '—'}
              </TableCell>
              <TableCell align="right" sx={{ color: row.LOW_RATING_COUNT > 0 ? '#c62828' : 'text.secondary' }}>
                {row.LOW_RATING_COUNT || 0}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── By Region tab ───────────────────────────────────────────────────────────

function RegionsTab({ data, loading }) {
  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data || !data.rows) return null;

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Region</TableCell>
            <TableCell align="right">Stores</TableCell>
            <TableCell align="right">Responses</TableCell>
            <TableCell align="right">Avg Hospitality</TableCell>
            <TableCell align="right">Avg Meal Quality</TableCell>
            <TableCell align="right">Avg Overall</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.rows.length === 0 ? (
            <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" py={2}>No responses match the current filters.</Typography></TableCell></TableRow>
          ) : data.rows.map(row => (
            <TableRow key={row.STORE_REGION} hover>
              <TableCell>{row.STORE_REGION || '—'}</TableCell>
              <TableCell align="right">{row.STORE_COUNT}</TableCell>
              <TableCell align="right">{row.RESPONSE_COUNT}</TableCell>
              <TableCell align="right" sx={{ color: ratingColor(row.AVG_HOSPITALITY), fontWeight: 600 }}>
                {row.AVG_HOSPITALITY ? Number(row.AVG_HOSPITALITY).toFixed(2) : '—'}
              </TableCell>
              <TableCell align="right" sx={{ color: ratingColor(row.AVG_MEAL_QUALITY), fontWeight: 600 }}>
                {row.AVG_MEAL_QUALITY ? Number(row.AVG_MEAL_QUALITY).toFixed(2) : '—'}
              </TableCell>
              <TableCell align="right" sx={{ color: ratingColor(row.AVG_OVERALL), fontWeight: 600 }}>
                {row.AVG_OVERALL ? Number(row.AVG_OVERALL).toFixed(2) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Responses tab ───────────────────────────────────────────────────────────

function ResponsesTab({
  data, loading, page, pageSize, onPageChange, onPageSizeChange,
  lowOnly, setLowOnly, withFeedback, setWithFeedback, search, setSearch,
  reviewedFilter, setReviewedFilter,
  onExport, exporting, onRowClick,
}) {
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Search email or feedback"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Review status</InputLabel>
          <Select
            value={reviewedFilter}
            label="Review status"
            onChange={e => setReviewedFilter(e.target.value)}
          >
            <MenuItem value=""><em>All</em></MenuItem>
            <MenuItem value="pending">Pending only</MenuItem>
            <MenuItem value="reviewed">Reviewed only</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Switch checked={lowOnly} onChange={e => setLowOnly(e.target.checked)} />}
          label="Low ratings only (≤2)"
        />
        <FormControlLabel
          control={<Switch checked={withFeedback} onChange={e => setWithFeedback(e.target.checked)} />}
          label="With feedback only"
        />
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          size="small"
          startIcon={exporting ? <CircularProgress size={14} /> : <FileDownloadIcon />}
          onClick={onExport}
          disabled={exporting || loading}
        >
          {exporting ? 'Exporting…' : 'Export XLSX'}
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Store</TableCell>
              <TableCell>Region</TableCell>
              <TableCell align="right">Hospitality</TableCell>
              <TableCell align="right">Meal Quality</TableCell>
              <TableCell>Feedback</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={20} /></TableCell></TableRow>
            ) : !data || data.rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="text.secondary" py={2}>No responses match the current filters.</Typography></TableCell></TableRow>
            ) : data.rows.map(row => (
              <TableRow key={row.RESPONSE_ID} hover sx={{ cursor: 'pointer' }} onClick={() => onRowClick(row)}>
                <TableCell>{fmtDate(row.SURVEY_COMPLETION_DATE)}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{row.CUSTOMER_EMAIL}</TableCell>
                <TableCell>{row.STORE_NAME || '—'}</TableCell>
                <TableCell>{row.STORE_REGION || '—'}</TableCell>
                <TableCell align="right">
                  <Chip
                    size="small"
                    label={row.HOSPITALITY_RATING}
                    sx={{ bgcolor: alpha(ratingColor(row.HOSPITALITY_RATING), 0.15), color: ratingColor(row.HOSPITALITY_RATING), fontWeight: 700 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Chip
                    size="small"
                    label={row.MEAL_QUALITY_RATING}
                    sx={{ bgcolor: alpha(ratingColor(row.MEAL_QUALITY_RATING), 0.15), color: ratingColor(row.MEAL_QUALITY_RATING), fontWeight: 700 }}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: 'text.secondary' }}>
                  {row.ADDITIONAL_FEEDBACK || '—'}
                </TableCell>
                <TableCell>
                  {row.REVIEWED_AT ? (
                    <Chip size="small" label="Reviewed" color="success" />
                  ) : (
                    <Chip size="small" label="Pending" variant="outlined" />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && data.total > 0 && (
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            onPageChange={(_, p) => onPageChange(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={e => onPageSizeChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        )}
      </TableContainer>
    </Box>
  );
}

// ─── Invitations tab ─────────────────────────────────────────────────────────

function InvitationsTab({ data, loading, page, pageSize, onPageChange, onPageSizeChange, pendingOnly, setPendingOnly }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <FormControlLabel
          control={<Switch checked={pendingOnly} onChange={e => setPendingOnly(e.target.checked)} />}
          label="Pending only (not completed)"
        />
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Sent</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Store ID</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reminder Sent</TableCell>
              <TableCell align="right">Days Since Sent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={20} /></TableCell></TableRow>
            ) : !data || data.rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center"><Typography variant="body2" color="text.secondary" py={2}>No invitations match the current filters.</Typography></TableCell></TableRow>
            ) : data.rows.map(row => (
              <TableRow key={row.INVITATION_ID} hover>
                <TableCell>{fmtDateTime(row.SENT_AT)}</TableCell>
                <TableCell sx={{ fontSize: 12 }}>{row.CUSTOMER_EMAIL}</TableCell>
                <TableCell align="right">{row.STORE_ID || '—'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.STATUS}
                    color={row.STATUS === 'COMPLETED' ? 'success' : 'default'}
                    variant={row.STATUS === 'COMPLETED' ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell>{row.REMINDER_SENT_AT ? fmtDate(row.REMINDER_SENT_AT) : <Typography variant="caption" color="text.secondary">—</Typography>}</TableCell>
                <TableCell align="right" sx={{ color: row.DAYS_SINCE_SENT > 7 && row.STATUS !== 'COMPLETED' ? '#c62828' : 'text.primary' }}>
                  {row.DAYS_SINCE_SENT ?? '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && data.total > 0 && (
          <TablePagination
            component="div"
            count={data.total}
            page={page}
            onPageChange={(_, p) => onPageChange(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={e => onPageSizeChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        )}
      </TableContainer>
    </Box>
  );
}

// ─── Response drawer ─────────────────────────────────────────────────────────

function ResponseDrawer({ row, open, onClose, onToggleReview, marking }) {
  const isReviewed = !!row?.REVIEWED_AT;
  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>Response detail</Typography>
        {row && (
          <Chip
            size="small"
            label={isReviewed ? 'Reviewed' : 'Pending review'}
            color={isReviewed ? 'success' : 'default'}
            variant={isReviewed ? 'filled' : 'outlined'}
            sx={{ mr: 1 }}
          />
        )}
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      {row && (
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">Date</Typography>
          <Typography variant="body2" mb={1.5}>{fmtDate(row.SURVEY_COMPLETION_DATE)}</Typography>

          <Typography variant="caption" color="text.secondary">Email</Typography>
          <Typography variant="body2" mb={1.5}>{row.CUSTOMER_EMAIL}</Typography>

          <Typography variant="caption" color="text.secondary">Store</Typography>
          <Typography variant="body2" mb={1.5}>
            {row.STORE_NAME || '—'}{row.STORE_REGION ? ` · ${row.STORE_REGION}` : ''}
            {row.DISTRICT_OPERATOR ? ` · ${row.DISTRICT_OPERATOR}` : ''}
          </Typography>

          <Divider sx={{ my: 2 }} />
          <RatingBar label="Hospitality" value={row.HOSPITALITY_RATING} />
          <RatingBar label="Meal Quality" value={row.MEAL_QUALITY_RATING} />

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">Additional Feedback</Typography>
          <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, bgcolor: 'grey.50' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {row.ADDITIONAL_FEEDBACK || '(no feedback)'}
            </Typography>
          </Paper>

          {row.ORDER_ID && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">Order ID</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.ORDER_ID}</Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />
          {isReviewed && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Reviewed by {row.REVIEWED_BY || 'unknown'} on {fmtDateTime(row.REVIEWED_AT)}
              </Typography>
            </Box>
          )}
          <Button
            fullWidth
            variant={isReviewed ? 'outlined' : 'contained'}
            color={isReviewed ? 'inherit' : 'success'}
            disabled={marking}
            onClick={() => onToggleReview(row, !isReviewed)}
            startIcon={marking ? <CircularProgress size={14} /> : null}
          >
            {marking ? 'Saving…' : (isReviewed ? 'Mark as pending' : 'Mark as reviewed')}
          </Button>
        </Box>
      )}
    </Drawer>
  );
}

// ─── main component ──────────────────────────────────────────────────────────

const defaultFilters = () => ({
  startDate: subDays(new Date(), 90),
  endDate: new Date(),
  store: '',
  region: '',
  storeType: '',
  districtOperator: '',
});

export default function NPSEmployeeDashboard() {
  const { user: firebaseUser } = useUserAuth();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  const [tab, setTab] = useState(0);
  const [filters, setFilters] = useState(defaultFilters());
  const [refreshKey, setRefreshKey] = useState(0);
  const [dimensions, setDimensions] = useState({});

  const [overview, setOverview] = useState(null);
  const [stores, setStores] = useState(null);
  const [regions, setRegions] = useState(null);

  const [responses, setResponses] = useState(null);
  const [responsesPage, setResponsesPage] = useState(0);
  const [responsesPageSize, setResponsesPageSize] = useState(50);
  const [lowOnly, setLowOnly] = useState(false);
  const [withFeedback, setWithFeedback] = useState(false);
  const [search, setSearch] = useState('');
  const [reviewedFilter, setReviewedFilter] = useState('');
  const [marking, setMarking] = useState(false);

  const [invitations, setInvitations] = useState(null);
  const [invitationsPage, setInvitationsPage] = useState(0);
  const [invitationsPageSize, setInvitationsPageSize] = useState(50);
  const [pendingOnly, setPendingOnly] = useState(true);

  const [drawerRow, setDrawerRow] = useState(null);

  const [loading, setLoading] = useState({ overview: false, stores: false, regions: false, responses: false, invitations: false });
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const setLoad = (key, val) => setLoading(l => ({ ...l, [key]: val }));

  const getToken = useCallback(async () => {
    if (!firebaseUser?.getIdToken) return null;
    return firebaseUser.getIdToken(true);
  }, [firebaseUser]);

  // Load filter dimensions once
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        if (!token) return;
        const { data } = await axios.get('/hd-api/nps-employee/dimensions', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDimensions(data);
      } catch (e) {
        console.error('[nps-employee dimensions]', e);
      }
    })();
  }, [getToken]);

  const loadOverview = useCallback(async (f) => {
    setLoad('overview', true);
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`/hd-api/nps-employee/scorecard?${buildParams(f)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOverview(data);
    } catch (e) {
      setError('Failed to load overview data.');
    } finally { setLoad('overview', false); }
  }, [getToken]);

  const loadStores = useCallback(async (f) => {
    setLoad('stores', true);
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`/hd-api/nps-employee/stores?${buildParams(f)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStores(data);
    } catch (e) {
      setError('Failed to load store data.');
    } finally { setLoad('stores', false); }
  }, [getToken]);

  const loadRegions = useCallback(async (f) => {
    setLoad('regions', true);
    try {
      const token = await getToken();
      if (!token) return;
      const { data } = await axios.get(`/hd-api/nps-employee/regions?${buildParams(f)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRegions(data);
    } catch (e) {
      setError('Failed to load region data.');
    } finally { setLoad('regions', false); }
  }, [getToken]);

  const loadResponses = useCallback(async (f, p, ps, opts) => {
    setLoad('responses', true);
    try {
      const token = await getToken();
      if (!token) return;
      const params = buildParams(f);
      params.set('page', p + 1);
      params.set('pageSize', ps);
      if (opts.lowOnly) params.set('lowOnly', '1');
      if (opts.withFeedback) params.set('withFeedback', '1');
      if (opts.search) params.set('search', opts.search);
      if (opts.reviewedFilter) params.set('reviewedFilter', opts.reviewedFilter);
      const { data } = await axios.get(`/hd-api/nps-employee/responses?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResponses(data);
    } catch (e) {
      setError('Failed to load responses.');
    } finally { setLoad('responses', false); }
  }, [getToken]);

  const handleToggleReview = useCallback(async (row, markReviewed) => {
    if (!row?.RESPONSE_ID) return;
    const token = await getToken();
    if (!token) return;
    setMarking(true);
    try {
      const url = `/hd-api/nps-employee/responses/${row.RESPONSE_ID}/review`;
      const headers = { Authorization: `Bearer ${token}` };
      const resp = markReviewed
        ? await axios.post(url, {}, { headers })
        : await axios.delete(url, { headers });
      const patch = markReviewed
        ? { REVIEWED_AT: resp.data?.REVIEWED_AT || new Date().toISOString(), REVIEWED_BY: resp.data?.REVIEWED_BY || firebaseUser?.email || 'you' }
        : { REVIEWED_AT: null, REVIEWED_BY: null };
      setDrawerRow(prev => (prev && prev.RESPONSE_ID === row.RESPONSE_ID ? { ...prev, ...patch } : prev));
      setResponses(prev => prev ? {
        ...prev,
        rows: prev.rows.map(r => r.RESPONSE_ID === row.RESPONSE_ID ? { ...r, ...patch } : r),
      } : prev);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update review status.');
    } finally {
      setMarking(false);
    }
  }, [getToken, firebaseUser]);

  const loadInvitations = useCallback(async (f, p, ps, opts) => {
    setLoad('invitations', true);
    try {
      const token = await getToken();
      if (!token) return;
      const params = new URLSearchParams();
      if (f.startDate) params.set('startDate', format(f.startDate, 'yyyy-MM-dd'));
      if (f.endDate) params.set('endDate', format(f.endDate, 'yyyy-MM-dd'));
      params.set('page', p + 1);
      params.set('pageSize', ps);
      if (opts.pendingOnly) params.set('pendingOnly', '1');
      const { data } = await axios.get(`/hd-api/nps-employee/invitations?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvitations(data);
    } catch (e) {
      setError('Failed to load invitations.');
    } finally { setLoad('invitations', false); }
  }, [getToken]);

  const handleExportResponses = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setExporting(true);
    setError(null);
    try {
      const params = buildParams(filters);
      params.set('export', '1');
      if (lowOnly) params.set('lowOnly', '1');
      if (withFeedback) params.set('withFeedback', '1');
      if (search) params.set('search', search);
      if (reviewedFilter) params.set('reviewedFilter', reviewedFilter);
      const { data } = await axios.get(`/hd-api/nps-employee/responses?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!data.rows || data.rows.length === 0) {
        setError('No responses match the current filters.');
        return;
      }
      const exportRows = data.rows.map(r => ({
        'Date': r.SURVEY_COMPLETION_DATE ? format(parseISO(r.SURVEY_COMPLETION_DATE), 'yyyy-MM-dd') : '',
        'Email': r.CUSTOMER_EMAIL || '',
        'Store ID': r.STORE_ID || '',
        'Store Name': r.STORE_NAME || '',
        'Region': r.STORE_REGION || '',
        'State': r.STORE_STATE || '',
        'Store Type': r.STORE_TYPE || '',
        'District Operator': r.DISTRICT_OPERATOR || '',
        'Hospitality Rating': r.HOSPITALITY_RATING ?? '',
        'Meal Quality Rating': r.MEAL_QUALITY_RATING ?? '',
        'Additional Feedback': r.ADDITIONAL_FEEDBACK || '',
        'Order ID': r.ORDER_ID || '',
        'Response ID': r.RESPONSE_ID || '',
      }));
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Employee NPS Responses');
      const from = filters.startDate ? format(filters.startDate, 'yyyy-MM-dd') : 'start';
      const to = filters.endDate ? format(filters.endDate, 'yyyy-MM-dd') : 'end';
      const rvTag = reviewedFilter ? `_${reviewedFilter}` : '';
      XLSX.writeFile(wb, `nps-employee-responses_${from}_to_${to}${rvTag}.xlsx`);
      if (data.truncated) {
        setError(`Export capped at ${data.cap.toLocaleString()} rows — narrow your filters to see the rest.`);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Export failed.');
    } finally {
      setExporting(false);
    }
  }, [filters, lowOnly, withFeedback, search, reviewedFilter, getToken]);

  // Refetch active tab data when filters change (via Apply) or tab changes
  useEffect(() => {
    setError(null);
    if (tab === 0) loadOverview(filters);
    if (tab === 1) loadStores(filters);
    if (tab === 2) loadRegions(filters);
    if (tab === 3) { setResponsesPage(0); loadResponses(filters, 0, responsesPageSize, { lowOnly, withFeedback, search, reviewedFilter }); }
    if (tab === 4) { setInvitationsPage(0); loadInvitations(filters, 0, invitationsPageSize, { pendingOnly }); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, refreshKey]);

  // Responses tab: refetch on pagination or in-tab toggle change
  useEffect(() => {
    if (tab !== 3) return;
    loadResponses(filters, responsesPage, responsesPageSize, { lowOnly, withFeedback, search, reviewedFilter });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responsesPage, responsesPageSize, lowOnly, withFeedback, reviewedFilter]);

  // Debounced search on the Responses tab
  useEffect(() => {
    if (tab !== 3) return;
    const t = setTimeout(() => {
      setResponsesPage(0);
      loadResponses(filters, 0, responsesPageSize, { lowOnly, withFeedback, search, reviewedFilter });
    }, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Invitations tab: refetch on pagination or pendingOnly toggle
  useEffect(() => {
    if (tab !== 4) return;
    loadInvitations(filters, invitationsPage, invitationsPageSize, { pendingOnly });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationsPage, invitationsPageSize, pendingOnly]);

  function handleApply() { setRefreshKey(k => k + 1); }
  function handleTabChange(_, v) { setTab(v); }

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          <Paper
            elevation={0}
            sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 2 }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                  borderRadius: 2,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <GroupsIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato', color: 'text.primary' }}>
                  Employee NPS Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  MendoEats employee survey data — hospitality &amp; meal quality
                </Typography>
              </Box>
            </Box>
          </Paper>

          <FilterBar
            filters={filters}
            setFilters={setFilters}
            dimensions={dimensions}
            onApply={handleApply}
            loading={loading.overview || loading.stores || loading.regions || loading.responses || loading.invitations}
          />

          {error && <Alert severity={error.startsWith('Export capped') ? 'warning' : 'error'} sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          <Paper variant="outlined" sx={{ mb: 2 }}>
            <Tabs value={tab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tab label="Overview" />
              <Tab label="By Store" />
              <Tab label="By Region" />
              <Tab label="Responses" />
              <Tab label="Invitations" />
            </Tabs>
            <Box sx={{ p: 2 }}>
              {tab === 0 && <OverviewTab data={overview} loading={loading.overview} />}
              {tab === 1 && <StoresTab data={stores} loading={loading.stores} />}
              {tab === 2 && <RegionsTab data={regions} loading={loading.regions} />}
              {tab === 3 && (
                <ResponsesTab
                  data={responses}
                  loading={loading.responses}
                  page={responsesPage}
                  pageSize={responsesPageSize}
                  onPageChange={setResponsesPage}
                  onPageSizeChange={(v) => { setResponsesPageSize(v); setResponsesPage(0); }}
                  lowOnly={lowOnly}
                  setLowOnly={(v) => { setLowOnly(v); setResponsesPage(0); }}
                  withFeedback={withFeedback}
                  setWithFeedback={(v) => { setWithFeedback(v); setResponsesPage(0); }}
                  search={search}
                  setSearch={setSearch}
                  reviewedFilter={reviewedFilter}
                  setReviewedFilter={(v) => { setReviewedFilter(v); setResponsesPage(0); }}
                  onExport={handleExportResponses}
                  exporting={exporting}
                  onRowClick={setDrawerRow}
                />
              )}
              {tab === 4 && (
                <InvitationsTab
                  data={invitations}
                  loading={loading.invitations}
                  page={invitationsPage}
                  pageSize={invitationsPageSize}
                  onPageChange={setInvitationsPage}
                  onPageSizeChange={(v) => { setInvitationsPageSize(v); setInvitationsPage(0); }}
                  pendingOnly={pendingOnly}
                  setPendingOnly={(v) => { setPendingOnly(v); setInvitationsPage(0); }}
                />
              )}
            </Box>
          </Paper>
        </Box>
      </Container>

      <ResponseDrawer
        row={drawerRow}
        open={!!drawerRow}
        onClose={() => setDrawerRow(null)}
        onToggleReview={handleToggleReview}
        marking={marking}
      />
    </div>
  );
}
